'use client'

import React from 'react'
import { Box, Burger, Flex, Group, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useMutation } from '@urql/next'
import { usePathname } from 'next/navigation'
import { useContext, useEffect } from 'react'

import { MarkNotificationAsRead } from '~/app/_graphql/MarkNotificationAsRead'
import {
  AccountNav,
  AppLayoutContext,
  HeaderLogo,
  IdentityContext,
  NotificationsListComponent,
  WaitForStableAuthState,
  fullHeader,
  menuItems,
  MobileMenuDrawer,
  AccountNavDrawerContent,
  DesktopMenuDrawer,
} from '../..'
import { NotificationsTabs } from '../../notifications/NotificationsTabs'
import { NotificationNav } from './NotificationNav'
import { burger, headerElement, headerLeftSectionBlock, headerLogoBlock, headerRightSectionBlock } from './styles.css'
import { useNavigation } from '../../auth/NavigationContext'

import type { Route } from 'next'
import type { MouseEvent, KeyboardEvent } from 'react'
import { appTrace } from '~/utils'

// Types
interface HeaderButtonProps {
  path: Route
  label: string
  testId?: string
  onClick?: (e: MouseEvent | KeyboardEvent) => void
}

interface NonVisibleNotificationsProps {
  shouldFetch: boolean
}

interface ClickableElementProps {
  children: React.ReactNode
  onClick?: (e: MouseEvent) => void
  onKeyDown?: (e: KeyboardEvent) => void
  style?: React.CSSProperties
  testId?: string
}

// Utility Components
const ClickableElement = ({ children, onClick, onKeyDown, style, testId }: ClickableElementProps) => (
  <div onClick={onClick} onKeyDown={onKeyDown} role="button" tabIndex={0} style={style} data-testid={testId}>
    {children}
  </div>
)

const NonVisibleNotifications = ({ shouldFetch }: NonVisibleNotificationsProps) => {
  if (!shouldFetch) return null
  return (
    <div style={{ display: 'none' }}>
      <NotificationsListComponent skipRendering={true} />
    </div>
  )
}

const HeaderButton = ({ path, label, testId, onClick }: HeaderButtonProps) => {
  const pathName = usePathname()
  const { mobile } = useContext(AppLayoutContext)
  const navigation = useNavigation()

  if (mobile) return null

  const handleClick = (e: MouseEvent | KeyboardEvent) => {
    onClick?.(e)
    navigation.navigate({ name: `header_${label.toLowerCase().replace(/\s+/g, '_')}`, route: path, event: e })
  }

  return (
    <Flex
      pl={label === 'Community' ? '0px' : 'md'}
      align="center"
      h="100%"
      className={headerElement}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick(e)}
      role="button"
      tabIndex={0}
      data-testid={testId || `header-${label.toLowerCase().replace(/\s+/g, '-')}-button`}
    >
      <Flex
        pb="sm"
        style={{
          borderBottom: pathName === path ? '1px solid #000' : undefined,
        }}
      >
        <Text size="lg" fw="600" pl="xs" tt="uppercase" lh="133.75%">
          {label}
        </Text>
      </Flex>
    </Flex>
  )
}

// Main Component
export function Header() {
  const { hiddenHeader, mobile } = useContext(AppLayoutContext)
  const { currentAccount } = useContext(IdentityContext)
  const pathName = usePathname()

  const [openedNotifications, { open: openNotifications, close: closeNotifications, toggle: toggleNotifications }] =
    useDisclosure(false)
  const [openedNav, { open: openNav, close: closeNav, toggle: toggleNav }] = useDisclosure(false)

  const [, update] = useMutation(MarkNotificationAsRead)

  const DrawerComponent = mobile ? MobileMenuDrawer : DesktopMenuDrawer

  useEffect(() => {
    closeNotifications()
    closeNav()
  }, [pathName])

  const onCloseNotifications = async () => {
    closeNotifications()
    await update({})
  }

  const handleOpenMenu = () => {
    closeNotifications()
    openNav()
  }

  if (hiddenHeader) return null

  return (
    <Group className={fullHeader} px={mobile ? 'md' : '20px'}>
      <Flex justify="space-between" w="100%">
        <div className={headerLeftSectionBlock}>
          {mobile && (
            <Burger
              opened={openedNav}
              onClick={() => {
                toggleNav()
                closeNotifications()
              }}
              data-testid="header-burger-button"
              classNames={{ burger }}
              style={{ position: 'relative', bottom: '5px', paddingLeft: '0px' }}
            />
          )}
          <HeaderButton path="/" label="Community" testId="header-community-button" />
          {/* <WaitForStableAuthState context="projects-header-link"> */}
          {/* {currentAccount && <HeaderButton path="/iso" label="In search of" testId="header-projects-button" />} */}
          <HeaderButton path="/iso" label="In search of" testId="header-projects-button" />
          {/* </WaitForStableAuthState> */}
        </div>

        <div className={headerLogoBlock}>
          <HeaderLogo />
        </div>

        <div className={headerRightSectionBlock}>
          <Box className={menuItems}>
            <ClickableElement
              onClick={() => {
                appTrace('toggleNav via header-account-nav')
                toggleNav()
                closeNotifications()
              }}
              onKeyDown={(e) => e.key === 'Enter' && closeNotifications()}
              style={{
                position: 'relative',
                bottom: '5px',
                borderBottom: pathName.includes(`accounts/${currentAccount?.id}`) ? '1px solid #000' : undefined,
              }}
              testId="header-account-nav"
            >
              <AccountNav openedNav={openedNav} closeNav={closeNav} openNav={openNav} />
            </ClickableElement>
            <WaitForStableAuthState context="notification-nav">
              {currentAccount && (
                <ClickableElement
                  style={{ position: 'relative', bottom: '3px' }}
                  onClick={() => {
                    appTrace('toggleNotifications via header-notification-nav')
                    closeNav()
                    toggleNotifications()
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && closeNotifications()}
                  testId="header-notification-nav"
                >
                  <NotificationNav
                    openedNotifications={openedNotifications}
                    closeNotifications={onCloseNotifications}
                    openNotifications={openNotifications}
                  />
                </ClickableElement>
              )}
            </WaitForStableAuthState>
          </Box>
          <WaitForStableAuthState context="non-vis-comp">
            <NonVisibleNotifications shouldFetch={!!currentAccount} />
          </WaitForStableAuthState>
        </div>
      </Flex>

      <>
        <DrawerComponent opened={openedNav} onClose={closeNav}>
          <AccountNavDrawerContent close={closeNav} />
        </DrawerComponent>

        <DrawerComponent opened={openedNotifications} onClose={onCloseNotifications} position="right">
          <NotificationsTabs />
        </DrawerComponent>
      </>
    </Group>
  )
}
